import React from 'react';
import classnames from 'classnames';
import { Button } from '@eventbrite/marmalade';
import FollowLoadingButton from './FollowLoadingButton';
import { gettext } from '../i18n';
import {
    STANDARD_BUTTON_STYLE,
    SOLITARY_BUTTON_STYLE,
    STANDARD_BUTTON_SIZE,
    type FollowButtonStyle,
    type FollowButtonSize,
} from '../constants/styles';
import styles from './FollowButton.module.scss';

export interface FollowButtonText {
    active: string;
    default: string;
}

export interface FollowButtonProps {
    /** Callback for when user clicks to follow. */
    onFollow: () => void;
    /** Callback for when user clicks to unfollow. */
    onUnFollow: () => void;
    /** Whether user is currently following the organizer. `null` = not-yet-known → skeleton. */
    isFollowing?: boolean | null;
    /** Whether the button can be clicked. */
    isDisabled?: boolean;
    /** Organizer name (button renders nothing until this is present). */
    organizerName?: string;
    /** Render-prop for a follower count (only shown when `numFollowers >= 10`). */
    renderFollowCount?: (numFollowers: number) => React.ReactNode;
    numFollowers?: number;
    /** 'standard' | 'solitary'. */
    style?: FollowButtonStyle;
    /** 'standard' | 'compact' | 'mini'. */
    size?: FollowButtonSize;
    /** Class for the button container. */
    __containerClassName?: string;
    /** Custom button labels. */
    text?: FollowButtonText;
    /** Metrics page-area tag (data attribute, was used by statsig). */
    pageArea?: string;
    /**
     * Optional analytics hook. Replaces eb-ui's `@eventbrite/statsig` `logEvent`
     * (dropped per C1) — the consuming app can wire Heap/Split here.
     */
    onTrack?: (action: 'Follow' | 'Unfollow') => void;
}

const isInfoReady = (isFollowing: boolean | null | undefined): boolean =>
    typeof isFollowing === 'boolean';

/**
 * Follow / following toggle button.
 *
 * Ported from eb-ui `FollowButton.jsx` (which wrapped `eds-follow-button` →
 * `eds-button`). Here it renders a Marmalade `Button`: solitary = filled brand
 * (`primary`), standard = outlined (`secondary`). Loading + empty-name guards
 * and the follow-count render-prop are preserved 1:1.
 */
const FollowButton = ({
    onFollow,
    onUnFollow,
    isFollowing = null,
    isDisabled,
    organizerName,
    renderFollowCount,
    numFollowers,
    style = STANDARD_BUTTON_STYLE,
    size = STANDARD_BUTTON_SIZE,
    __containerClassName,
    text = { active: gettext('Following'), default: gettext('Follow') },
    pageArea,
    onTrack,
}: FollowButtonProps) => {
    const followButtonText = isFollowing ? text.active : text.default;
    const followButtonDataSpec = isFollowing
        ? 'following-organizer-button'
        : 'follow-organizer-button';

    const followButtonAction = () => {
        if (isFollowing) {
            onUnFollow();
            onTrack?.('Unfollow');
        } else {
            onFollow();
            onTrack?.('Follow');
        }
    };

    const shouldRenderFollowCount =
        (numFollowers ?? 0) >= 10 && !!renderFollowCount;

    if (!isInfoReady(isFollowing)) {
        return <FollowLoadingButton size={size} />;
    }

    if (!organizerName) {
        return null;
    }

    const buttonClasses = classnames(
        styles['follow-button'],
        styles[`follow-button--${size}`],
        {
            [styles['follow-button--solitary']]:
                style === SOLITARY_BUTTON_STYLE,
            [styles['follow-button--following']]: isFollowing,
        },
    );

    return (
        <>
            <div
                className={__containerClassName}
                data-follow-page-area={pageArea}
            >
                <Button
                    variant={
                        style === SOLITARY_BUTTON_STYLE ? 'primary' : 'secondary'
                    }
                    disabled={isDisabled}
                    data-spec={followButtonDataSpec}
                    onClick={followButtonAction}
                    className={buttonClasses}
                >
                    {followButtonText}
                </Button>
            </div>
            {shouldRenderFollowCount &&
                renderFollowCount!(numFollowers as number)}
        </>
    );
};

export default FollowButton;
